import type { RuleConditions } from '@/api/promotions'

/**
 * Einheitlicher Formatter für Promotion-Rule-Conditions.
 * Unterstützt BEIDE persistierten Formate:
 * - flach (api/promotions.ts: minClicks, minOrders, maxAcos, …)
 * - nested {and:[...]}/{or:[...]} — das Format, das der Backend-Evaluator
 *   (promotions/service.ts::evaluateConditions) auswertet und das die geseedeten
 *   Bestandsregeln tragen. Integrations-Fix (Codex-Review Welle-2-Merge G, P2):
 *   der Nested-Zweig stammt aus dem in G2 entfernten RuleConfig-Renderer — ohne ihn
 *   zeigte /rules für alle Bestandsregeln nur '—', nachdem der Promotions-Tab
 *   (die einzige Nested-fähige Ansicht) auf /rules umgeleitet wurde.
 * Ersetzt die zwei divergierenden lokalen Renderer (describeConditions in RulesView,
 * formatConditions im frueheren Promotions-Rules-Tab).
 */
export function formatRuleConditions(conditions: RuleConditions | null | undefined): string {
  if (!conditions || typeof conditions !== 'object') return '—'
  const c = conditions as Record<string, unknown>

  if (Array.isArray(c.and) || Array.isArray(c.or)) {
    const opLabels: Record<string, string> = { gte: '≥', lte: '≤', gt: '>', lt: '<', eq: '=' }
    const formatSingle = (entry: Record<string, Record<string, unknown>>): string =>
      Object.entries(entry)
        .map(([field, ops]) =>
          Object.entries(ops)
            .map(([op, val]) => `${field} ${opLabels[op] ?? op} ${val}`)
            .join(' + '),
        )
        .join(', ')
    if (Array.isArray(c.and)) {
      return c.and.map(e => formatSingle(e as Record<string, Record<string, unknown>>)).join(' UND ')
    }
    return (c.or as unknown[]).map(e => formatSingle(e as Record<string, Record<string, unknown>>)).join(' ODER ')
  }

  const parts: string[] = []
  if (typeof c.minClicks === 'number') parts.push(`Clicks ≥ ${c.minClicks}`)
  if (typeof c.minOrders === 'number') parts.push(`Orders ≥ ${c.minOrders}`)
  if (typeof c.maxAcos === 'number') parts.push(`ACoS ≤ ${c.maxAcos}%`)
  if (typeof c.minAcos === 'number') parts.push(`ACoS ≥ ${c.minAcos}%`)
  if (typeof c.minSpend === 'number') parts.push(`Spend ≥ ${c.minSpend}€`)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

/** Cooldown einheitlich in Stunden (gerundet). Backend-Feld ist cooldownMinutes. */
export function formatCooldownHours(cooldownMinutes: number): string {
  return `${Math.round(cooldownMinutes / 60)}h`
}
